import React, { useEffect } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCapIcon, BookOpenIcon, HeartIcon, UsersIcon, ShieldCheckIcon, ClockIcon, DollarSignIcon, AwardIcon, CheckCircleIcon, ArrowRightIcon, StarIcon, QuoteIcon, CheckIcon } from 'lucide-react';
import { getFindDegreesTemplate } from '../data/findDegreesTemplates';
import { getProgramTemplate, programsIndexSlug } from '../data/programTemplates';
import { ProgramTemplatePage } from './ProgramTemplatePage';
import { ProgramDomainsPage } from './ProgramDomainsPage';
import { usePageMeta } from '../hooks/usePageMeta';
import { buildFindDegreesClickout } from '../services/mediaMatchers/clickout';
import { getDegreeProgramCombo } from '../utils/degreeSlugs';
import { DegreeProgramLandingPage } from './DegreeProgramLandingPage';
import { ProgramInformationComboPage } from './ProgramInformationComboPage';
const benefitIconMap: Record<string, React.ElementType> = {
  GraduationCapIcon,
  BookOpenIcon,
  HeartIcon,
  UsersIcon,
  ShieldCheckIcon,
  ClockIcon,
  DollarSignIcon,
  AwardIcon
};
/**
 * Resolves a slug to the right template: the program index, a domain page, an
 * individual program page, or the original lead-gen landing template. Explicit
 * routes are declared in App.tsx (so each URL shows up on its own), which means
 * the slug is read from the path when there is no `:template` param to read.
 *
 * Program content lives under `/program-information/`; anything reaching the
 * legacy `/find-degrees/` path with a program slug is redirected there so old
 * links keep working.
 */
export function FindDegreesPage() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const pathSlug = location.pathname.split('/').filter(Boolean).pop() || '';
  const slug = (template || pathSlug).toLowerCase();
  const isLegacyPath = location.pathname.startsWith('/find-degrees/');
  const program = getProgramTemplate(slug);
  const isProgramContent = slug === programsIndexSlug || !!program;
  if (isLegacyPath && isProgramContent) {
    return <Navigate to={`/program-information/${slug}${location.search}`} replace data-id="element-1706" data-mp="2migbco3v76qq" />;
  }
  if (slug === programsIndexSlug) {
    return <ProgramDomainsPage data-id="element-1707" data-mp="2cneknntxxwm1" />;
  }
  if (program) {
    return <ProgramTemplatePage key={program.slug} program={program} data-id="element-1708" data-mp="30c7hrnmauai8" />;
  }
  // Generated degree-level × program landing pages, e.g.
  // /find-degrees/bachelors-business-administration
  const combo = getDegreeProgramCombo(slug);
  if (combo) {
    // Code-form URLs (e.g. /find-degrees/3-100) redirect to the readable
    // canonical slug so there is one indexable URL per combination. Query
    // parameters carry through untouched, and the redirect stays on whichever
    // of the two paths the visitor arrived on.
    const base = isLegacyPath ? '/find-degrees' : '/program-information';
    if (slug !== combo.slug) {
      return <Navigate to={`${base}/${combo.slug}${location.search}`} replace data-id="element-1709" data-mp="1clrn6mpwhqb3" />;
    }
    // Under /program-information the combination gets the program-page layout
    // with the in-hero match form; under /find-degrees it keeps the
    // click-out landing page.
    return isLegacyPath ? <DegreeProgramLandingPage key={combo.slug} combo={combo} data-id="element-1710" data-mp="1hd9ppf7knyy6" /> : <ProgramInformationComboPage key={combo.slug} combo={combo} data-id="element-1711" data-mp="tnp1ohwkabi1" />;
  }
  return <FindDegreesLandingTemplate data-id="element-1712" data-mp="tps11sjltgzf" />;
}
function FindDegreesLandingTemplate() {
  const {
    template
  } = useParams<{
    template: string;
  }>();
  const location = useLocation();
  const data = template ? getFindDegreesTemplate(template) : undefined;
  usePageMeta(data?.metaTitle || 'Find Your Degree | OnlineCollegeGuide', data?.metaDescription || 'Find accredited online degree programs that fit your goals.', {
    ogType: 'website'
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [template]);
  if (!data) {
    return <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4" data-id="element-1713" data-mp="jmemj8xsf0w4">
        <h1 className="text-3xl font-bold text-navy mb-4" data-id="element-1714" data-mp="okzcmfjbs46u">Page Not Found</h1>
        <p className="text-gray-600 mb-8" data-id="element-1715" data-mp="sl9qsfh9u0ws">
          We couldn't find the page you're looking for.
        </p>
        <Link to="/" className="text-white bg-navy px-6 py-2 rounded-md hover:bg-navy-light transition-colors" data-id="element-1716" data-mp="xlxiz4h8knsl">
          Return Home
        </Link>
      </div>;
  }
  const accent = data.accentColor;
  const accentDark = data.accentColorDark;
  // The CTAs click out to the Media Matchers search endpoint, forwarding every
  // query parameter on the current URL.
  const ctaHref = buildFindDegreesClickout(location.search);
  return <main className="min-h-screen bg-white" data-id="element-1717" data-mp="1lagtlqnrdcnu">
      {/* Minimal Top Bar */}
      <div className="bg-white border-b border-gray-100" data-id="element-1718" data-mp="1l8ceeckesr89">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between" data-id="element-1719" data-mp="1qala3goo27e2">
          <Link to="/" className="flex items-center" data-id="element-1720" data-mp="20ei80tchx03u">
            <img src="https://cdn.magicpatterns.com/uploads/hM6pwxGTxH7R3Ug6xJ6sm8/Horizontal_OCG_logo.png" alt="Online College Guide - Your Path Forward" className="h-10 w-auto object-contain" data-id="element-1721" data-mp="1vedosvk7dr9x" />
          </Link>
          <div className="hidden sm:flex items-center text-xs font-semibold text-gray-500 tracking-wider uppercase" data-id="element-1722" data-mp="1mfihfg5fab51">
            <ShieldCheckIcon className="w-4 h-4 mr-1.5 text-green-600" data-id="element-1723" data-mp="2j5stuw3d6mov" />
            Trusted by 2M+ Students
          </div>
        </div>
      </div>

      {/* Hero - light theme matching reference layout */}
      <section className="bg-gray-50 relative overflow-hidden" data-id="element-1724" data-mp="25cjnl4apqhak">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20" data-id="element-1725" data-mp="25c1siazse948">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center" data-id="element-1726" data-mp="31vhi66cpn4v9">
            {/* Left: Copy */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} data-id="element-1727" data-mp="2x2xycrnfjdi0">
              <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[11px] font-semibold uppercase tracking-widest text-white mb-6" style={{
              backgroundColor: '#14479E'
            }} data-id="element-1728" data-mp="lfupg7krrx3e">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: '#8FBEF5'
              }} data-id="element-1729" data-mp="hftyxokebvfz" />
                {data.heroEyebrowBadge || data.eyebrow}
              </span>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] text-gray-900 mb-6 tracking-tight" data-id="element-1730" data-mp="1wvsrhrhj5iv4">
                {data.heroHeadlinePrefix && <>{data.heroHeadlinePrefix} </>}
                {data.heroHeadlineHighlight && <span style={{
                color: '#14479E'
              }} data-id="element-1731" data-mp="3md7xhdivh55m">
                    {data.heroHeadlineHighlight}
                  </span>}
                {data.heroHeadlineSuffix && <>
                    <br data-id="element-1732" data-mp="3ra6k7b07i9ts" />
                    {data.heroHeadlineSuffix}
                  </>}
                {!data.heroHeadlinePrefix && !data.heroHeadlineHighlight && !data.heroHeadlineSuffix && data.headline}
              </h1>

              <p className="text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed" data-id="element-1733" data-mp="iv6h2fzcam3u">
                {data.subheadline}
              </p>
            </motion.div>

            {/* Right: Step indicator + Card */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} data-id="element-1734" data-mp="2kpf9dzfj40a3">
              {/* Step Indicator */}
              <div className="flex items-center justify-between mb-5 px-2 sm:px-4" data-id="element-1735" data-mp="23ldpfolk04go">
                <div className="flex items-center gap-2.5" data-id="element-1736" data-mp="28incixlh041c">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#1668E0'
                }} data-id="element-1737" data-mp="1awg7xnp62boz">
                    <CheckIcon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1738" data-mp="1ktcis02jf9wp" />
                  </div>
                  <span className="text-sm font-semibold text-gray-900" data-id="element-1739" data-mp="1puigydeyvpdm">
                    {data.heroStep1Label || 'Profile Complete'}
                  </span>
                </div>
                <div className="flex-1 h-px bg-gray-300 mx-3 sm:mx-4" data-id="element-1740" data-mp="317uniw242m3h" />
                <div className="flex items-center gap-2.5" data-id="element-1741" data-mp="plakgubtu3mj">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#14479E'
                }} data-id="element-1742" data-mp="pkr0l82m955v">
                    <span className="text-xs font-bold text-white" data-id="element-1743" data-mp="awe5edbcrqcf">2</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-500" data-id="element-1744" data-mp="5xbavmt012ip">
                    {data.heroStep2Label || 'Confirm Email'}
                  </span>
                </div>
              </div>

              {/* Card */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10 text-center" data-id="element-1745" data-mp="3t1hh4gvtr0lt">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2" data-id="element-1746" data-mp="3o3y0xbfkgdr8">
                  {data.heroCardHeading || data.headline}
                </h2>
                {data.heroCardSubtext && <p className="text-sm font-semibold mb-6" style={{
                color: '#14479E'
              }} data-id="element-1747" data-mp="1mcupijxuqv5m">
                    {data.heroCardSubtext}
                  </p>}
                <a href={ctaHref} className="group inline-flex items-center justify-center text-white font-bold py-3.5 px-8 rounded-lg text-base transition-all duration-200 shadow-md hover:shadow-lg" style={{
                backgroundColor: '#FF9F04'
              }} onMouseEnter={e => {
                ;
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#E08C00';
              }} onMouseLeave={e => {
                ;
                (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#FF9F04';
              }} data-id="element-1748" data-mp="365gh81hsprcv">
                  {data.ctaLabel}
                  <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1749" data-mp="1qnxyng97eeir" />
                </a>
                {data.heroCardHelper && <p className="mt-6 text-xs sm:text-sm text-gray-500 leading-relaxed max-w-xs mx-auto" data-id="element-1750" data-mp="1lc18ujblll0n">
                    {data.heroCardHelper}
                  </p>}
              </div>

              {/* Trust strip below card */}
              <div className="mt-6 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-gray-500" data-id="element-1751" data-mp="1bittc5n58omu">
                {data.trustItems.map(item => <div key={item} className="flex items-center" data-id="element-1752" data-mp="249bwt7rs4s0s">
                    <CheckCircleIcon className="w-3.5 h-3.5 text-green-600 mr-1.5 flex-shrink-0" data-id="element-1753" data-mp="1pdcr23s573yt" />
                    {item}
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 lg:py-20 bg-white" data-id="element-1754" data-mp="iw94bbu54omg">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1755" data-mp="iy2bcs8o1auz">
          <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-1756" data-mp="2ljfsu6hg1sbt">
            <h2 className="text-2xl sm:text-3xl font-bold mb-3" style={{
            color: accent
          }} data-id="element-1757" data-mp="2lkz93x33o3dl">
              {data.benefitsHeading}
            </h2>
            <div className="w-16 h-1 mx-auto rounded-full" style={{
            backgroundColor: '#1668E0'
          }} data-id="element-1758" data-mp="26ujl6ajauhbq" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1759" data-mp="1xtlgdq11e5s3">
            {data.benefits.map((benefit, i) => {
            const Icon = benefitIconMap[benefit.icon] || BookOpenIcon;
            return <motion.div key={i} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md transition-shadow" data-id="element-1760" data-mp="1nw7ecvfv1jjb" data-mpi={i}>
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{
                backgroundColor: `${accent}15`
              }} data-id="element-1761" data-mp="8bewq861z6fl">
                    <Icon className="w-6 h-6" style={{
                  color: accent
                }} data-id="element-1762" data-mp="3bv00thdkn2t6" />
                  </div>
                  <h3 className="text-lg font-bold mb-2" style={{
                color: accent
              }} data-id="element-1763" data-mp="3lp04rfx4a20b">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1764" data-mp="yni6nqbfqupc">
                    {benefit.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Featured Schools */}
      <section className="py-14 bg-gray-50 border-y border-gray-200" data-id="element-1765" data-mp="13ltiu7p7dhaq">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1766" data-mp="13nmszwz0yjnh">
          <h2 className="text-center text-sm font-bold uppercase tracking-widest mb-8 text-gray-500" data-id="element-1767" data-mp="1hk084zg368ys">
            {data.schoolsHeading}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-1768" data-mp="1rb4lxvu0zns1">
            {data.schoolNames.map(name => <div key={name} className="bg-white border border-gray-200 rounded-lg p-4 text-center flex items-center justify-center min-h-[80px]" data-id="element-1769" data-mp="1mbrqimp398du">
                <span className="text-sm font-semibold leading-tight" style={{
              color: accent
            }} data-id="element-1770" data-mp="1wb0gl4gu22ux">
                  {name}
                </span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      {data.testimonial && <section className="py-16 lg:py-20 bg-white" data-id="element-1771" data-mp="2awxriffwnikj">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1772" data-mp="2avcy4t5bu5o9">
            <QuoteIcon className="w-12 h-12 mx-auto mb-6 opacity-30" style={{
          color: accent
        }} data-id="element-1773" data-mp="1w6yyer37ixbx" />
            <div className="flex items-center justify-center gap-1 mb-4" data-id="element-1774" data-mp="1n7lmlrrm1wo1">
              {[0, 1, 2, 3, 4].map(i => <StarIcon key={i} className="w-5 h-5 fill-current" style={{
            color: '#5FA0EE'
          }} data-id="element-1775" data-mp="1s5n5ocp1dvzi" />)}
            </div>
            <p className="text-xl sm:text-2xl font-medium leading-relaxed mb-6 italic" style={{
          color: accent
        }} data-id="element-1776" data-mp="1i7h3rv38cpun">
              "{data.testimonial.quote}"
            </p>
            <p className="font-bold text-gray-900" data-id="element-1777" data-mp="pdklu9yf388b">{data.testimonial.name}</p>
            <p className="text-sm text-gray-500" data-id="element-1778" data-mp="pbhlavzn1wpc">{data.testimonial.detail}</p>
          </div>
        </section>}

      {/* Bottom CTA */}
      <section className="py-16 lg:py-20" style={{
      backgroundColor: accentDark
    }} data-id="element-1779" data-mp="1i1ghrcg5ww41">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1780" data-mp="1d3y5pd6um0rt">
          <h2 className="text-2xl sm:text-4xl font-extrabold text-white mb-4 leading-tight" data-id="element-1781" data-mp="183ajd4tke83x">
            {data.bottomCtaHeading}
          </h2>
          <p className="text-base sm:text-lg text-gray-300 mb-8 leading-relaxed" data-id="element-1782" data-mp="137ldkjqybuz6">
            {data.bottomCtaBody}
          </p>
          <a href={ctaHref} className="group inline-flex items-center justify-center text-white font-bold py-4 px-10 rounded-lg text-base tracking-wide transition-all duration-200 shadow-lg hover:shadow-xl" style={{
          backgroundColor: '#FF9F04'
        }} onMouseEnter={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#E08C00';
        }} onMouseLeave={e => {
          ;
          (e.currentTarget as HTMLAnchorElement).style.backgroundColor = '#FF9F04';
        }} data-id="element-1783" data-mp="3nuycsq9ddihz">
            {data.bottomCtaLabel}
            <ArrowRightIcon className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" data-id="element-1784" data-mp="2r52xtaav8ek1" />
          </a>
          <p className="mt-5 text-xs text-gray-400 uppercase tracking-widest" data-id="element-1785" data-mp="3115yebq2k54v">
            Complementary • No Obligation • Takes 2 Minutes
          </p>
        </div>
      </section>

      {/* Footer mini */}
      <footer className="bg-gray-900 text-gray-400 text-xs py-6" data-id="element-1786" data-mp="16mwo83e1okvp">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2" data-id="element-1787" data-mp="16neqbgne1tbj">
          <p data-id="element-1788" data-mp="38j42g0pheo0p">
            &copy; {new Date().getFullYear()} OnlineCollegeGuide. All rights
            reserved.
          </p>
          <p className="max-w-3xl mx-auto leading-relaxed" data-id="element-1789" data-mp="33jrhjli67r0r">
            Advertiser Disclosure: OnlineCollegeGuide.com is an
            advertising-supported site. Featured schools compensate us, which
            does not influence our editorial content.
          </p>
          <div className="flex justify-center gap-4 pt-2" data-id="element-1790" data-mp="2pmcng3gz2v2g">
            <Link to="/privacy-policy" className="hover:text-white transition-colors" data-id="element-1791" data-mp="2kn9mov80zb0a">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="hover:text-white transition-colors" data-id="element-1792" data-mp="2ukxh1k1pyghx">
              Terms of Service
            </Link>
          </div>
        </div>
      </footer>
    </main>;
}